/**
 * Maps allocation recommendation tags → fund-card insight rows.
 *
 * Belongs here: default tag copy/visual and optional per-card visual remap.
 * Does not belong here: card layout or CTA wiring.
 */
import { formatRupeeLabel } from '../../../../display-values/lumpsumPlanAmount'
import { LumpsumAllocationInsightTag } from '../../../api/allocation.types'
import {
  PlanFundInsightVisual,
  type PlanFundCardInsight
} from '../../lumpsum-ui-components/PlansFundCard'

type AllocationInsightTagConfig = {
  visual: PlanFundInsightVisual
  text: (context: { categoryName: string; missedGains: number }) => string
}

const ALLOCATION_INSIGHT_TAG_CONFIG: Record<LumpsumAllocationInsightTag, AllocationInsightTagConfig> = {
  // Sell
  [LumpsumAllocationInsightTag.MISSED_GAINS]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ missedGains }) => `${formatRupeeLabel({ rawAmount: missedGains })} missed gains`
  },
  [LumpsumAllocationInsightTag.POOR_PERFORMANCE]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: () => 'Poor performance'
  },
  [LumpsumAllocationInsightTag.INSIGNIFICANT_ALLOCATION]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: () => 'Insignificant allocation in this fund'
  },
  [LumpsumAllocationInsightTag.TOO_MANY_FUNDS]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `Too many funds in ${categoryName}`
  },
  [LumpsumAllocationInsightTag.CATEGORY_NOT_RECOMMENDED]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `${categoryName} not recommended`
  },
  [LumpsumAllocationInsightTag.HIGH_EXPOSURE]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `High exposure in ${categoryName}`
  },
  [LumpsumAllocationInsightTag.CONCENTRATION_RISK]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: () => 'Concentration risk in this fund'
  },
  [LumpsumAllocationInsightTag.NEAR_CONCENTRATION_RISK]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: () => 'Near concentration risk in this fund'
  },
  [LumpsumAllocationInsightTag.REALLOCATED_PREFERENCE]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: () => 'Reallocated as per your preference'
  },
  // Buy — defaults keep INFO blue / REBALANCE orange; buy cards remap orange → green
  [LumpsumAllocationInsightTag.TOP_RANKED_FUND]: {
    visual: PlanFundInsightVisual.INFO,
    text: () => 'Top ranked fund in its category'
  },
  [LumpsumAllocationInsightTag.INCREASES_EXPOSURE]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `Increases exposure in ${categoryName}`
  },
  [LumpsumAllocationInsightTag.IMPROVES_DIVERSIFICATION]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `Improves diversification in ${categoryName}`
  },
  [LumpsumAllocationInsightTag.IMPROVES_PERFORMANCE]: {
    visual: PlanFundInsightVisual.REBALANCE,
    text: ({ categoryName }) => `Improves performance in ${categoryName}`
  },
  // Under review
  [LumpsumAllocationInsightTag.PARTIAL_SELL_LOCKIN]: {
    visual: PlanFundInsightVisual.INFO,
    text: () => 'Partial sell due to lock-in restrictions'
  },
  [LumpsumAllocationInsightTag.PARTIAL_SELL_TAX]: {
    visual: PlanFundInsightVisual.INFO,
    text: () => 'Partial sell due to tax restrictions'
  }
}

/**
 * Buy-card insight tone: keep INFO (blue), map REBALANCE (orange) → POSITIVE (green).
 * Same idea as rebalance buy insights using POSITIVE.
 */
export function toBuyAllocationInsightVisual(
  defaultVisual: PlanFundInsightVisual
): PlanFundInsightVisual {
  if (defaultVisual === PlanFundInsightVisual.REBALANCE) {
    return PlanFundInsightVisual.POSITIVE
  }
  return defaultVisual
}

/** Builds insight rows from backend tags. Defaults come from tag config; cards may remap visuals. */
export function buildAllocationInsightsFromTags({
  tags,
  isin,
  categoryName,
  missedGains = 0,
  resolveVisual
}: {
  tags?: LumpsumAllocationInsightTag[] | null
  isin: string
  categoryName: string
  missedGains?: number | null
  resolveVisual?: (
    defaultVisual: PlanFundInsightVisual,
    tag: LumpsumAllocationInsightTag
  ) => PlanFundInsightVisual
}): PlanFundCardInsight[] {
  const safeCategoryName = categoryName || 'this category'
  const safeMissedGains = missedGains ?? 0

  return (tags ?? []).flatMap((tag) => {
    const config = ALLOCATION_INSIGHT_TAG_CONFIG[tag]
    if (!config) return []

    const defaultVisual = config.visual
    return [
      {
        id: `${isin}-${tag}`,
        text: config.text({ categoryName: safeCategoryName, missedGains: safeMissedGains }),
        visual: resolveVisual ? resolveVisual(defaultVisual, tag) : defaultVisual
      }
    ]
  })
}
